import React, { useRef, useEffect, useContext } from "react";
import { Avatar, Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useModel } from "hooks/useModel";
import styles from "./ChatArea.module.scss";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import EXTMarkdown from "components/common/EXTMarkdown";
import ChatAreaInput, { ChatAreaInputRef } from "../ChatInput/ChatAreaInput";
import ChatAreaMessageThinkingBubble from "./ChatAreaMessageThinkingBubble";
import { EmojiObjects as LightbulbIcon } from "@mui/icons-material";
import { AccountCircle } from "@mui/icons-material";
import { AuthProvider } from "pages";
import { EMAILID_COOKIE, MOCK_USER_COOKIE, ROLE } from "constant";
import { Cookies } from "utils";
import MarkdownDownloadBtn from "components/common/MarkdownToPdf/MarkdownDownloadBtn";
import MarkdownToPPTBtn from "components/common/MarkdownToPPT";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const ChatAreaMessage = () => {
  const {
    chatlist,
    selectedKey,
    fullScreen,
    setFullScreen,
    setPromptsOpen,
    setPromptConfig,
    drawerWidth,
  } = useModel("aichat");
  const scrollRef = useRef<HTMLDivElement>(null);
  const isUserScrollingRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const scrollTimeoutRef = useRef<number | null>(null);
  const chatInputRef = useRef<ChatAreaInputRef>(null);
  const authDetail = useContext(AuthProvider);
  const isPartner = authDetail?.role === ROLE.PARTNER_ROLE;
  const mockEmail = Cookies.get(MOCK_USER_COOKIE);
  const email = Cookies.get(EMAILID_COOKIE);
  const [username] = (mockEmail || email || "@").split("@");

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedKey, chatlist.find((item) => item.id === selectedKey)?.content?.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceToBottom <= 200) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatlist.find((item) => item.id === selectedKey)?.content]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const threshold = 120;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceToBottom <= threshold;

    isUserScrollingRef.current = true;
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 200);
  };

  return (
    <Box
      className={styles.ChatAreaMessage}
      ref={scrollRef}
      onScroll={handleScroll}
    >
      <Box
        className={styles.chatAreaMessageContent}
        style={{
          width: fullScreen ? "100%" : drawerWidth,
        }}
      >
        <div className={styles.chatMessagesList}>
          {chatlist
            .find((item) => item.id === selectedKey)
            ?.content?.map((msg, index) => (
              <Box
                className={styles.chatMessage}
                key={(msg?.id || "") + index}
              >
                <div className={styles.avatar}>
                  {msg.role === "user" && isPartner && <AccountCircle />}
                  {msg.role === "user" && !isPartner && (
                    <Avatar
                      className={styles.ciscoavatar}
                      src={
                        username
                          ? `https://wwwin.cisco.com/dir/photo/zoom/${username}.jpg`
                          : `/avatar.svg`
                      }
                    >
                      {username?.charAt(0) || ""}
                    </Avatar>
                  )}
                  {msg.role !== "user" && (
                    <img
                      src={"/portfolio/circuitcharacter_neutral_light.svg"}
                      alt=""
                    />
                  )}
                </div>
                <div className={styles.message}>
                  {msg.loading ? (
                    <div className={styles.content}>
                      <ChatAreaMessageThinkingBubble
                        key={index}
                        loading={msg.loading}
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        className={styles.content}
                        style={
                          msg.role === "user"
                            ? {
                                backgroundColor: "#0070d214",
                              }
                            : {}
                        }
                      >
                        {!!msg?.prompt && (
                          <Box
                            component="span"
                            sx={{
                              alignItems: "center",
                              gap: 0.4,
                              backgroundColor: "rgba(245, 158, 11, 0.12)",
                              color: "#f59e0b",
                              padding: "1px 4px",
                              borderRadius: "3px",
                              fontSize: "13px",
                              fontWeight: 600,
                              lineHeight: 1.4,
                              verticalAlign: "top",
                              marginRight: "6px",
                              border: `1px solid ${"rgba(245, 158, 11, 0.25)"}`,
                            }}
                          >
                            <LightbulbIcon
                              sx={{
                                fontSize: "14px",
                                verticalAlign: "middle",
                              }}
                            />
                            <Box
                              component="span"
                              sx={{ verticalAlign: "middle" }}
                            >
                              PROMPT
                            </Box>
                          </Box>
                        )}
                        <EXTMarkdown content={msg.content || ""}></EXTMarkdown>
                      </div>
                      <div className={styles.footer}>
                        <div>
                          {msg.role === "assistant" && (
                            <MarkdownToPPTBtn
                              material={msg.content || ""}
                              tooltipTitle="Generate as Powerpoint"
                              loadingChildren={
                                <IconButton
                                  size="small"
                                  disabled
                                >
                                  <CircularProgress
                                    size={14}
                                    thickness={6}
                                  />
                                </IconButton>
                              }
                            >
                              <IconButton size="small">
                                <div
                                  style={{
                                    width: 18,
                                    height: 18,
                                    paddingBottom: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src="/AIPPTIcon.png"
                                    alt=""
                                    style={{
                                      maxWidth: 24,
                                      width: "auto",
                                      height: "100%",
                                      display: "block",
                                      objectFit: "contain",
                                    }}
                                  />
                                </div>
                              </IconButton>
                            </MarkdownToPPTBtn>
                          )}
                          {msg.role === "assistant" && (
                            <MarkdownDownloadBtn
                              content={msg.content || ""}
                              fileName={"message-content.pdf"}
                              loadingChildren={
                                <IconButton
                                  size="small"
                                  disabled
                                >
                                  <CircularProgress
                                    size={14}
                                    thickness={6}
                                  />
                                </IconButton>
                              }
                            >
                              <Tooltip title="Download as PDF">
                                <IconButton size="small">
                                  <PictureAsPdfIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </MarkdownDownloadBtn>
                          )}
                          {msg.role === "user" && (
                            <Tooltip title="Regenerate Response">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  chatInputRef.current?.sendMessage(msg.content, msg.prompt)
                                }
                              >
                                <RefreshIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Copy Message">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(msg.content || "");
                                toast.success("Copied to clipboard");
                              }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add to Favorite Prompts">
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (!fullScreen) setFullScreen(true);
                                setPromptsOpen(true);
                                setPromptConfig({
                                  state: "New",
                                  favorite: true,
                                  promptContent: msg?.prompt?.prompt || msg.content || "",
                                  promptDescription: msg?.prompt?.description || "",
                                  promptIcon: "lightbulb",
                                  promptTitle: msg?.prompt?.title || "",
                                  promptType: "personal",
                                  promptVisibility: "public",
                                  id: null,
                                });
                              }}
                            >
                              <PlaylistAddIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </div>
                        {msg.role === "user" ? null : <>{msg.time}</>}
                      </div>
                    </>
                  )}
                </div>
              </Box>
            ))}
        </div>
        <Box className={styles.textarea}>
          <ChatAreaInput ref={chatInputRef} />
        </Box>
      </Box>
    </Box>
  );
};

export default ChatAreaMessage;
